import axios from '@/axios'
import firebase from 'firebase/app'

/**
 * @description Get BPM API URL
 * @param {String} [path] Path to append to the base URL
 * @returns {string}
 */
export const getBPMTsApiUrl = (path = '') => {
  return process.env.NODE_ENV === 'production' ? `https://bpm-api.cloudify.biz${path}` : `http://localhost:2000${path}`
}

/**
 * @description Get Lighthouse URL
 * @param {String} [path] Path to append to the base URL
 * @returns {string}
 */
export const geLighthouseUrl = (path = '') => {
  return process.env.NODE_ENV === 'production' ? `https://app.cloudify.biz${path}` : `http://localhost:8080${path}`
}

/**
 * @description Get BPM API URL to generate token with query attached
 * @param {Object} query Query params to attach to URL
 * @returns {string}
 */
export const getBPMTsGenerateTokenUrl = (query) => {
  if (process.env.VUE_APP_SERVERTYPE === 'production') {
    return `${getBPMTsApiUrl()}/oauth2/generateToken?${new URLSearchParams(query).toString()}`
  } else {
    return `${getBPMTsApiUrl()}/oauth2/generateToken?${new URLSearchParams({ ...query, staging: true }).toString()}`
  }
}

/**
 * @description Get BPM API URL to auth url with query attached
 * @param {Object} query Query params to attach to URL
 * @returns {string}
 */
export const getBPMTsAuthUrl = (query) => {
  if (process.env.VUE_APP_SERVERTYPE === 'production') {
    return `${getBPMTsApiUrl()}/oauth2/authUrl?${new URLSearchParams(query).toString()}`
  } else {
    return `${getBPMTsApiUrl()}/oauth2/authUrl?${new URLSearchParams({ ...query, staging: true }).toString()}`
  }
}

/**
 * @description Get BPM API URL for HMAC SHA256 hash with query attached
 * @param {Object} query Query params to attach to URL
 * @returns {string}
 */
export const getBPMTsHashUrl = (query) => {
  if (process.env.VUE_APP_SERVERTYPE === 'production') {
    return `${getBPMTsApiUrl()}/oauth2/hash?${new URLSearchParams(query).toString()}`
  } else {
    return `${getBPMTsApiUrl()}/oauth2/hash?${new URLSearchParams({ ...query, staging: true }).toString()}`
  }
}

/**
 * @description Get app credentials revoker URL
 * @param {Object} params Query params to attach to URL
 * @returns {string}
 */
export const getBPMTsRevokeTokenUrl = (params) => {
  if (process.env.VUE_APP_SERVERTYPE === 'production') {
    return `${getBPMTsApiUrl()}/oauth2/revoke?${new URLSearchParams(params).toString()}`
  } else {
    return `${getBPMTsApiUrl()}/oauth2/revoke?${new URLSearchParams({ ...params, staging: true }).toString()}`
  }
}

/**
 * @description Get API key from local storage
 * @returns {string}
 */
export const getApiKey = () => {
  return JSON.parse(localStorage.getItem('userInfo')).uid
}

/**
 * @description Get Database key from local storage
 * @returns {string}
 */
export const getDatabaseKey = () => {
  return JSON.parse(localStorage.getItem('userInfo')).database
}
/**
 * @description Store credentials to firestor credential object
 * @param {*} appSlug Slug of the app the credentials are for
 * @param {*} object Object to store
 * @returns {Promise<void>}
 */
export const updateCredentialsToFirestore = async (appSlug, object) => {
  await firebase
    .firestore()
    .collection('cloudify')
    .doc(JSON.parse(localStorage.getItem('userInfo')).uid)
    .set(
      {
        credentials: {
          [appSlug]: object,
        },
      },
      { merge: true }
    )
}

/**
 * @description Get CMS API Url with path appended.
 * @param {string} [path] Path to append to the base URL
 * @returns {string}
 */
export const getCMSUrl = (path = '') => {
  return `https://cms.cloudify.biz/api${path}`
}

/**
 * @return {string}
 */
export const getPlansFromCms = (tagType) => {
  return `${getCMSUrl()}/plans?populate=icon,parameters,stripeTest.billingCycleMonthly.currency,stripeTest.billingCycleMonthly.multiPriceIds,stripeTest.billingCycleYearly.currency,stripeTest.billingCycleYearly.multiPriceIds,stripeProduction.billingCycleMonthly.currency,stripeProduction.billingCycleMonthly.multiPriceIds,stripeProduction.billingCycleYearly.currency,stripeProduction.billingCycleYearly.multiPriceIds,&filters[type][$eq]=${tagType}&pagination[limit]=1000&sort=position`
}

/**
 * @description Get URl for getting lighthouse supported apps from CMS
 * @param {Object} query Query params to attach to URL
 * @returns {string}
 */
export const getLighthouseSupportedAppsUrl = (appName) => {
  if (appName) {
    return `${getCMSUrl()}/lighthouse-supported-apps?populate=fieldsRequired,logo&filters[slug][$eq]=${appName}&pagination[limit]=1000`
  } else {
    return `${getCMSUrl()}/lighthouse-supported-apps?populate=fieldsRequired,logo&pagination[limit]=1000`
  }
}

/**
 * @description Get Autocode URL
 * @param {String} [path] Path to append to the base URL
 * @returns {string}
 */
export const getAutocodeUrl = (path = '') => {
  return process.env.NODE_ENV === 'production' ? `https://sk97.api.stdlib.com${path}` : `http://localhost:8170${path}`
}

/**
 * This function is responsible for getting the hostname and then return the appropriate url for autocode workflow
 * @returns {String} This is the url which we will use to call the autocode workflow
 */
export const getStripeForCloudifyUrl = () => {
  const APP_SERVERTYPE = process.env.VUE_APP_SERVERTYPE
  if (APP_SERVERTYPE === 'production') {
    //for production
    return 'https://us-central1-bpm-cloudify.cloudfunctions.net/stripeForCloudify'
  } else if (APP_SERVERTYPE === 'staging') {
    //for preview url
    return 'https://us-central1-lighthousestaging-e14af.cloudfunctions.net/stripeForCloudify'
  } else {
    //for local testing
    return 'http://127.0.0.1:5005/lighthousestaging-e14af/us-central1/stripeForCloudify'
  }
}

/**
 * This function is responsible for error reporting for all the failed entries
 * @returns {String} This is the url which we will use to call the autocode workflow
 */
export const errorReportingUrl = () => {
  const APP_SERVERTYPE = process.env.VUE_APP_SERVERTYPE
  if (APP_SERVERTYPE === 'production') {
    //for production
    return 'https://us-central1-bpm-cloudify.cloudfunctions.net/errorReporting'
  } else if (APP_SERVERTYPE === 'staging') {
    //for preview url
    return 'https://us-central1-lighthousestaging-e14af.cloudfunctions.net/errorReporting'
  } else {
    //for local testing
    return 'http://127.0.0.1:5001/bpm-cloudify/us-central1/errorReporting'
  }
}

/**
 * @description Get Autocode creds verification URL
 * @returns {string}
 */
export const getAutocodeCredsVerifyUrl = () => {
  return `${getAutocodeUrl()}/creds-verifier`
}

/**
 * @description Get hash value from BPM API & match it with given hash.
 * @param {Object} params
 * @param {string} params.appSlug Slug of the app the the hmac is for
 * @param {string} params.apiKey
 * @param {string} params.message
 * @param {string} params.hmac
 * @returns
 */
export const matchHmac = async ({ hmac, message, apiKey, appSlug }) => {
  const { hash } = (await axios.get(getBPMTsHashUrl({ message, apiToken: apiKey, appName: appSlug }))).data
  return hash === hmac
}

/**
 * This function gets a epoch time in milliseconds and returns a human readable string if the human readable flag is set to true
 * @param {Number} epoch the epoch time to convert to date
 * @param {*} humanReadable the human readable date format in ISO
 * @returns
 */
export const epochToDate = (epoch, humanReadable) => {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

  const dateObject = new Date(epoch * 1000)
  if (humanReadable) {
    const month = monthNames[dateObject.getMonth()]
    const date = dateObject.getDate()
    const year = dateObject.getFullYear()
    return `${month} ${date},${year}`
  } else {
    return dateObject.toISOString()
  }
}

/**
 * This function is responsible for returning the appropriate url for autocode workflow
 * @returns {String} This is the url which we will use to call the autocode workflow
 */
export const getAutocodeUrlForDeletingLHAccounts = () => {
  const APP_SERVERTYPE = process.env.VUE_APP_SERVERTYPE
  if (APP_SERVERTYPE === 'production') {
    //for production
    return 'https://us-central1-bpm-cloudify.cloudfunctions.net/deleteLighthouseUser'
  } else if (APP_SERVERTYPE === 'staging') {
    //for preview url
    return 'https://us-central1-lighthousestaging-e14af.cloudfunctions.net/deleteLighthouseUser'
  } else {
    //for local testing
    return 'http://127.0.0.1:5001/bpm-cloudify/us-central1/deleteLighthouseUser'
  }
}

/**
 * This function is used to get the currency symbol for the provided currency
 */
export const getCurrencySymbol = (currency) => {
  const currencySymbolLookup = {
    dkk: 'Kr.',
    usd: '$',
    eur: '€',
  }
  return currencySymbolLookup[currency.toLowerCase()]
}
